import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import ExternalApiRequest from './../../../../utils/ExternalApiRequest';

const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 3001 }),
    menu: (base) => ({ ...base, zIndex: 3001 }),
};

const AsyncSelectField = ({
    id,
    label,
    name,
    placeholder,
    value,
    onChange,
    labelClass,
    isDynamic = false,
    multiple = false,
    required = false,
    hint, // ✅ Hint popup support
}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedValues, setSelectedValues] = useState(
        isDynamic && multiple ? [[]] : multiple ? [] : null
    );

    const [showHint, setShowHint] = useState(false);
    const hintRef = useRef(null);
    const labelClasses = `${labelClass || ''} mb-0`;

    /* ---------------- CLOSE HINT ON OUTSIDE CLICK ---------------- */
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (hintRef.current && !hintRef.current.contains(e.target)) {
                setShowHint(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /* ---------------- INPUT CHANGE ---------------- */
    const handleInputChange = async (inputValue) => {
        if (!inputValue) {
            setOptions([]);
            return;
        }
        setLoading(true);
        try {
            const fetchedOptions = await fetchOptions(inputValue);
            setOptions(fetchedOptions);
        } catch (error) {
            console.error('Error fetching options:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- HANDLE CHANGE ---------------- */
    const handleChange = (selectedOption, actionMeta) => {
        if (isDynamic && multiple) {
            const updatedValues = [...selectedValues];
            updatedValues[updatedValues.length - 1] = selectedOption || [];
            setSelectedValues(updatedValues);
            onChange(id, updatedValues);
        } else if (multiple) {
            const values = selectedOption
                ? selectedOption.map((opt) => opt.value)
                : [];
            setSelectedValues(values);
            onChange(id, values);
        } else {
            const value = selectedOption ? selectedOption.value : '';
            setSelectedValues(value);
            onChange(id, value);
        }
    };

    /* ---------------- FETCH OPTIONS ---------------- */
    const fetchOptions = async (query) => {
        const options = {
            api: 'enableConfiguration',
            urlAppend: `/${query}`,
        };
        const response = await ExternalApiRequest(options);
        return response;
    };

    /* ---------------- DYNAMIC HANDLERS ---------------- */
    const handleAddField = () => {
        const updatedValues = [...selectedValues, []];
        setSelectedValues(updatedValues);
        onChange(id, updatedValues);
    };

    const handleRemoveField = (index) => {
        const updatedValues = selectedValues.filter((_, idx) => idx !== index);
        setSelectedValues(updatedValues);
        onChange(id, updatedValues);
    };

    /* ---------------- RENDER ---------------- */
    return (
        <div className="form-group">
            {label && (
                <div className="d-inline-flex align-items-center gap-2 mb-1 position-relative flex-nowrap">
                    <label htmlFor={id} className={labelClasses}>
                        {label}
                        {required && <span className="text-danger ms-1">*</span>}
                    </label>

                    {hint && (
                        <div className="position-relative" ref={hintRef}>
                            <span
                                role="button"
                                tabIndex={0}
                                className="text-muted"
                                style={{ cursor: 'pointer' }}
                                aria-label="Show hint"
                                onClick={() => setShowHint((prev) => !prev)}
                                onKeyDown={(e) => e.key === 'Enter' && setShowHint((prev) => !prev)}
                            >
                                ℹ️
                            </span>

                            {showHint && (
                                <div
                                    className="position-absolute bg-white border shadow rounded p-2"
                                    style={{
                                        top: '120%',
                                        left: 0,
                                        width: '260px',
                                        zIndex: 3000,
                                    }}
                                >
                                    <div className="small text-muted">{hint}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {isDynamic && multiple ? (
                <div>
                    {selectedValues.map((group, index) => (
                        <div key={index} className="mb-2">
                            <Select
                                id={`${id}-${index}`}
                                name={name}
                                value={group}
                                onInputChange={handleInputChange}
                                onChange={(selected) =>
                                    handleChange(selected, { action: 'select-option' })
                                }
                                options={options}
                                isLoading={loading}
                                isClearable
                                required={required}
                                placeholder={placeholder}
                                isMulti
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={selectStyles}
                            />
                            <button
                                type="button"
                                className="btn btn-danger btn-sm mt-2"
                                onClick={() => handleRemoveField(index)}
                            >
                                Remove
                            </button>
                        </div>
                    ))}

                    <button
                        type="button"
                        className="btn btn-success btn-sm mt-2"
                        onClick={handleAddField}
                    >
                        Add Another Field
                    </button>
                </div>
            ) : (
                <Select
                    id={id}
                    name={name}
                    value={
                        multiple
                            ? options.filter((o) => selectedValues.includes(o.value))
                            : options.find((o) => o.value === selectedValues) || null
                    }
                    onInputChange={handleInputChange}
                    onChange={handleChange}
                    options={options}
                    isLoading={loading}
                    isClearable
                    placeholder={placeholder}
                    isMulti={multiple}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                />
            )}
        </div>
    );
};

export default AsyncSelectField;
